import { create } from 'zustand';
import {
  IOnboardingFlow,
  IOnboardingFlowRevision,
  OnboardingFlowNode,
  OnboardingFlowTransition,
  FlowValidationError,
  IUserTraversal,
} from '@scale/shared/onboardingControlPanel';
import { BannerProps } from '@scale/scaleui';
import moment from 'moment';
import { IExtendedSignupFlow } from '@scale/shared-types';

type InfoBannerData = Pick<BannerProps, 'cta' | 'buttonVariant' | 'variant' | 'title' | 'subtitle'>;

interface OCPStore {
  // Current flow is used to keep track of current shown flow's id, name, and version - don't overwrite its contents directly
  currentFlow: IOnboardingFlow | null;
  setCurrentFlow: (flow: IOnboardingFlow | null) => void;
  currentVersion: number;
  setCurrentVersion: (version: number) => void;
  flowVersions: { [id: string]: { [version: string]: IOnboardingFlow } };
  addFlowVersion: (flow: IOnboardingFlow | IOnboardingFlowRevision<string>) => void;
  addFlowVersions: (flows: IOnboardingFlow[] | IOnboardingFlowRevision<string>[]) => {
    [id: string]: { [version: string]: IOnboardingFlow };
  };
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  detailPanelOpen: boolean;
  signupLinks: string[];
  setSignupLinks: (links: string[]) => void;
  toggleDetailPanel: () => void;
  detailTarget: OnboardingFlowNode | OnboardingFlowTransition | null;
  setDetailTarget: (target: OnboardingFlowNode | OnboardingFlowTransition | null) => void;
  autofocusFlowName: boolean;
  toggleAutofocusFlowName: (b?: boolean) => void;
  validationResults: { isValid: boolean; errors: FlowValidationError[] };
  setValidationResults: (results: { isValid: boolean; errors: FlowValidationError[] }) => void;
  showFailureNodes: boolean;
  toggleShowFailureNodes: () => void;
  testFunnelModalOpen: boolean;
  toggleTestFunnelModal: (b: boolean) => void;
  infoBannerData: InfoBannerData | null;
  setInfoBannerData: (data: InfoBannerData | null) => void;
  searchText: string;
  setSearchText: (text: string) => void;
  userTraversal: IUserTraversal | null;
  setUserTraversal: (data: IUserTraversal | null) => void;
  flowFilterTags: { value: string; label: string }[];
  setFlowFilterTags: (tags: { value: string; label: string }[]) => void;
  flowFilterDates: Date[];
  setFlowFilterDates: (dates: Date[]) => void;
  unsavedSignupFlow: Partial<IExtendedSignupFlow>;
  setUnsavedSignupFlow: (signupFlow: Partial<IExtendedSignupFlow>) => void;
  manuallySetSignupFlow: boolean;
  setManuallySetSignupFlow: (b: boolean) => void;
  selectedSignupFlowId: string;
  setSelectedSignupFlowId: (id: string) => void;
  finishedFetchingSignupFlowDraft: boolean;
  setFinishedFetchingSignupFlowDraft: (b: boolean) => void;
  publishingDraft: boolean;
  setPublishingDraft: (b: boolean) => void;
  isValidSignupFlowName: boolean;
  setIsValidSignupFlowName: (b: boolean) => void;
  changedSignupFlowOnboardingFlow: boolean;
  setChangedSignupFlowOnboardingFlow: (b: boolean) => void;
  canFreeEdit: boolean;
  setCanFreeEdit: (b: boolean) => void;
}

const defaultTimeFrame = () => {
  const today = moment.utc().toDate();
  const weekPrior = moment.utc().subtract(7, 'days').toDate();
  return [weekPrior, today];
};

export const useOCPStore = create<OCPStore>((set, get) => ({
  currentFlow: null,
  setCurrentFlow: flow => {
    set({
      currentFlow: flow,
      currentVersion: flow?.version ?? 0,
      userTraversal: null,
    });
  },
  currentVersion: 0,
  setCurrentVersion: version => set({ currentVersion: version }),
  flowVersions: {},
  addFlowVersion: flow => {
    const versionedFlow: any = { ...flow };
    const flows = { ...get().flowVersions };
    if ((flow as IOnboardingFlowRevision<string>).originalFlowId)
      versionedFlow._id = (flow as IOnboardingFlowRevision<string>).originalFlowId;
    if (!flows[versionedFlow._id])
      flows[versionedFlow._id] = {
        [flow.version]: flow,
      };
    else flows[versionedFlow._id][versionedFlow.version] = flow;
    set({ flowVersions: flows });
  },
  addFlowVersions: newFlows => {
    const flows = { ...get().flowVersions };
    newFlows.forEach(flow => {
      const versionedFlow: any = { ...flow };
      if ((flow as IOnboardingFlowRevision<string>).originalFlowId)
        versionedFlow._id = (flow as IOnboardingFlowRevision<string>).originalFlowId;
      if (!flows[versionedFlow._id])
        flows[versionedFlow._id] = {
          [flow.version]: flow,
        };
      else flows[versionedFlow._id][versionedFlow.version] = flow;
    });
    set({ flowVersions: flows });
    return flows;
  },
  sidebarOpen: false,
  toggleSidebar: () => {
    if (get().sidebarOpen) set({ sidebarOpen: false });
    else set({ sidebarOpen: true, detailPanelOpen: false });
  },
  detailPanelOpen: false,
  signupLinks: [],
  setSignupLinks: links => set({ signupLinks: links }),
  toggleDetailPanel: () => {
    if (get().detailPanelOpen) set({ detailPanelOpen: false });
    else set({ detailPanelOpen: true, sidebarOpen: false });
  },
  detailTarget: null,
  setDetailTarget: target => set({ detailTarget: target }),
  autofocusFlowName: false,
  toggleAutofocusFlowName: b => {
    if (b !== undefined) set({ autofocusFlowName: b });
    else set({ autofocusFlowName: !get().autofocusFlowName });
  },
  validationResults: { isValid: true, errors: [] },
  setValidationResults: results => set({ validationResults: results }),
  showFailureNodes: true,
  toggleShowFailureNodes: () => {
    set({ showFailureNodes: !get().showFailureNodes });
  },
  testFunnelModalOpen: false,
  toggleTestFunnelModal: b => set({ testFunnelModalOpen: b }),
  infoBannerData: null,
  setInfoBannerData: (data: InfoBannerData | null) => {
    set({ infoBannerData: data });
  },
  searchText: '',
  setSearchText: text => set({ searchText: text }),
  userTraversal: null,
  setUserTraversal: data => set({ userTraversal: data }),
  flowFilterTags: [],
  setFlowFilterTags: tags => set({ flowFilterTags: tags }),
  flowFilterDates: defaultTimeFrame(),
  setFlowFilterDates: dates => set({ flowFilterDates: dates }),
  unsavedSignupFlow: {},
  setUnsavedSignupFlow: signupFlow => set({ unsavedSignupFlow: signupFlow }),
  manuallySetSignupFlow: false,
  setManuallySetSignupFlow: b => set({ manuallySetSignupFlow: b }),
  selectedSignupFlowId: '',
  setSelectedSignupFlowId: id => set({ selectedSignupFlowId: id }),
  finishedFetchingSignupFlowDraft: false,
  setFinishedFetchingSignupFlowDraft: b => set({ finishedFetchingSignupFlowDraft: b }),
  publishingDraft: false,
  setPublishingDraft: b => set({ publishingDraft: b }),
  isValidSignupFlowName: true,
  setIsValidSignupFlowName: b => set({ isValidSignupFlowName: b }),
  changedSignupFlowOnboardingFlow: false,
  setChangedSignupFlowOnboardingFlow: b => set({ changedSignupFlowOnboardingFlow: b }),
  canFreeEdit: false,
  setCanFreeEdit: b => set({ canFreeEdit: b }),
}));
